if (!customElements.get('gm-flavor-showcase')) {
	class GmFlavorShowcase extends HTMLElement {
		static #MOBILE_BREAKPOINT_PX = 767;
		static #BASE_SPEED_SCALE = 0.5;
		static #MOBILE_MAX_DRIFT_PX = 0;
		static #DESKTOP_MAX_DRIFT_PX = 100;
		static #DRIFT_VAR = '--flavor-showcase-drift-x';
		static #ATC_ANNOUNCE_DELAY_MS = 200;

		#parallaxItems = [];
		#rafId = null;
		#driftCleared = false;
		#reducedMotion = false;
		#atcBar = null;
		#atcResizeObserver = null;
		#atcRafId = null;
		#atcTrack = null;
		#atcPrev = null;
		#atcNext = null;
		#atcCounter = null;
		#atcStatus = null;
		#atcPreviews = [];
		#atcCount = 0;
		#atcScrollRafId = null;
		#atcAnnounceTimer = null;
		#atcLastAnnounced = 0;
		#onScrollOrResize = () => this.#scheduleUpdate();
		#onAtcReserve = () => this.#scheduleAtcReserve();

		connectedCallback() {
			this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			this.#initFloatingAtc();

			if (this.#reducedMotion) {
				return;
			}

			this.#parallaxItems = this.#collectParallaxItems();
			if (!this.#parallaxItems.length) {
				return;
			}

			window.addEventListener('scroll', this.#onScrollOrResize, { passive: true });
			window.addEventListener('resize', this.#onScrollOrResize);
		}

		#initFloatingAtc() {
			this.#atcBar = this.querySelector('.flavor-showcase__floating-atc');
			if (!this.#atcBar) {
				return;
			}

			// Reserve page-bottom space so the fixed bar never sits over footer
			// content or a focused element beneath it (WCAG 2.4.11). A ResizeObserver
			// keeps the reservation accurate as the bar's height changes.
			this.#reserveFloatingAtcSpace();
			if ('ResizeObserver' in window) {
				this.#atcResizeObserver = new ResizeObserver(() => this.#scheduleAtcReserve());
				this.#atcResizeObserver.observe(this.#atcBar);
			} else {
				window.addEventListener('resize', this.#onAtcReserve);
			}

			this.#initFloatingAtcCarousel();
		}

		#scheduleAtcReserve() {
			if (this.#atcRafId === null) {
				this.#atcRafId = requestAnimationFrame(() => {
					this.#atcRafId = null;
					this.#reserveFloatingAtcSpace();
				});
			}
		}

		#reserveFloatingAtcSpace() {
			if (!this.#atcBar) {
				return;
			}

			// Reserve the bar's full footprint — height plus its real gap to the
			// viewport bottom, which includes env(safe-area-inset-bottom) on notched
			// devices — so nothing beneath it can be obscured.
			const rect = this.#atcBar.getBoundingClientRect();
			const bottomGap = Math.max(0, window.innerHeight - rect.bottom);
			const reserved = Math.round(rect.height + bottomGap + 16);
			document.body.style.paddingBottom = `${reserved}px`;
			document.documentElement.style.scrollPaddingBottom = `${reserved}px`;
		}

		// Floating add-to-cart carousel; controls are hidden via CSS when not needed
		#initFloatingAtcCarousel() {
			this.#atcTrack = this.querySelector('[data-atc-track]');
			this.#atcPrev = this.querySelector('[data-atc-prev]');
			this.#atcNext = this.querySelector('[data-atc-next]');
			if (!this.#atcTrack || !this.#atcPrev || !this.#atcNext) {
				return;
			}

			this.#atcCounter = this.querySelector('[data-atc-counter]');
			this.#atcStatus = this.querySelector('[data-atc-status]');
			this.#atcPreviews = Array.from(this.querySelectorAll('[data-atc-preview]'));
			this.#atcCount = this.#atcTrack.children.length;
			this.#atcLastAnnounced = this.#atcCardIndex();

			this.#atcPrev.addEventListener('click', () => this.#scrollToAdjacentAtcCard(-1));
			this.#atcNext.addEventListener('click', () => this.#scrollToAdjacentAtcCard(1));
			for (const preview of this.#atcPreviews) {
				preview.addEventListener('click', () => this.#scrollToAtcCard(Number(preview.dataset.atcIndex)));
			}
			this.#atcTrack.addEventListener('scroll', () => this.#onAtcTrackScroll(), { passive: true });

			this.#syncAtcControls();
		}

		#atcCardIndex() {
			const rawIndex = Math.round(this.#atcTrack.scrollLeft / this.#atcTrack.clientWidth) || 0;
			return Math.max(0, Math.min(this.#atcCount - 1, rawIndex));
		}

		#scrollToAdjacentAtcCard(direction) {
			this.#scrollToAtcCard(this.#atcCardIndex() + direction);
		}

		#scrollToAtcCard(targetIndex) {
			const index = this.#atcCardIndex();
			const target = Math.max(0, Math.min(this.#atcCount - 1, targetIndex));
			if (target === index || Number.isNaN(target)) {
				return;
			}

			const behavior = this.#reducedMotion ? 'auto' : 'smooth';
			this.#atcTrack.scrollTo({ left: target * this.#atcTrack.clientWidth, behavior });
		}

		#onAtcTrackScroll() {
			if (this.#atcScrollRafId === null) {
				this.#atcScrollRafId = requestAnimationFrame(() => {
					this.#atcScrollRafId = null;
					this.#syncAtcControls();
				});
			}

			clearTimeout(this.#atcAnnounceTimer);
			this.#atcAnnounceTimer = setTimeout(
				() => this.#announceAtcCard(),
				GmFlavorShowcase.#ATC_ANNOUNCE_DELAY_MS
			);
		}

		// aria-disabled (not the disabled property) keeps the arrows focusable,
		// so keyboard focus is never lost when an end arrow deactivates
		#syncAtcControls() {
			const index = this.#atcCardIndex();
			this.#atcPrev.setAttribute('aria-disabled', String(index <= 0));
			this.#atcNext.setAttribute('aria-disabled', String(index >= this.#atcCount - 1));
			if (this.#atcCounter) {
				this.#atcCounter.textContent = `${index + 1} / ${this.#atcCount}`;
			}
			this.#atcPreviews.forEach((preview, previewIndex) => {
				preview.setAttribute('aria-current', String(previewIndex === index));
			});
		}

		// Announce once scrolling settles and only when the card actually changed,
		// so each navigation announces exactly once
		#announceAtcCard() {
			if (!this.#atcStatus) {
				return;
			}

			const index = this.#atcCardIndex();
			if (index === this.#atcLastAnnounced) {
				return;
			}

			this.#atcLastAnnounced = index;
			this.#atcStatus.textContent = `Product ${index + 1} of ${this.#atcCount}`;
		}

		disconnectedCallback() {
			window.removeEventListener('scroll', this.#onScrollOrResize);
			window.removeEventListener('resize', this.#onScrollOrResize);
			window.removeEventListener('resize', this.#onAtcReserve);

			if (this.#atcResizeObserver) {
				this.#atcResizeObserver.disconnect();
				this.#atcResizeObserver = null;
			}
			if (this.#atcRafId !== null) {
				cancelAnimationFrame(this.#atcRafId);
				this.#atcRafId = null;
			}
			if (this.#atcScrollRafId !== null) {
				cancelAnimationFrame(this.#atcScrollRafId);
				this.#atcScrollRafId = null;
			}
			clearTimeout(this.#atcAnnounceTimer);
			this.#atcPreviews = [];
			if (this.#atcBar) {
				document.body.style.paddingBottom = '';
				document.documentElement.style.scrollPaddingBottom = '';
				this.#atcBar = null;
			}

			if (this.#rafId !== null) {
				cancelAnimationFrame(this.#rafId);
				this.#rafId = null;
			}
			this.#parallaxItems = [];
		}

		#collectParallaxItems() {
			const itemEls = this.querySelectorAll('[data-parallax-speed]');

			// Pre-compute speeds so we avoid parsing on every scroll tick
			return Array.from(itemEls, (el) => ({
				el,
				speed: (parseFloat(el.dataset.parallaxSpeed) || 0) * GmFlavorShowcase.#BASE_SPEED_SCALE,
			}));
		}

		#scheduleUpdate() {
			if (this.#rafId === null) {
				this.#rafId = requestAnimationFrame(() => this.#applyDrift());
			}
		}

		#maxDrift() {
			const isMobile = window.innerWidth <= GmFlavorShowcase.#MOBILE_BREAKPOINT_PX;
			return isMobile ? GmFlavorShowcase.#MOBILE_MAX_DRIFT_PX : GmFlavorShowcase.#DESKTOP_MAX_DRIFT_PX;
		}

		#applyDrift() {
			this.#rafId = null;

			const maxDrift = this.#maxDrift();
			if (maxDrift === 0) {
				this.#clearDrift();
				return;
			}

			this.#driftCleared = false;
			const scrollY = window.scrollY;
			for (const { el, speed } of this.#parallaxItems) {
				const clamped = Math.max(-maxDrift, Math.min(maxDrift, scrollY * speed));
				el.style.setProperty(GmFlavorShowcase.#DRIFT_VAR, `${clamped}px`);
			}
		}

		// One-shot clear of any drift left over from a desktop-to-mobile resize
		#clearDrift() {
			if (this.#driftCleared) {
				return;
			}

			this.#driftCleared = true;
			for (const { el } of this.#parallaxItems) {
				el.style.removeProperty(GmFlavorShowcase.#DRIFT_VAR);
			}
		}
	}

	customElements.define('gm-flavor-showcase', GmFlavorShowcase);
}
